import { useState, useEffect } from "react";
import { PencilSimple, Copy, ArrowsClockwise, Check, CalendarBlank, Image, PaperPlaneTilt, Trash, Clock, WarningCircle, CheckCircle, Lightning, Database, ShieldCheck, GoogleLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const WEEKLY_SCHEDULE = [
  { day: "Monday", templates: ["trade_not_close", "would_you_accept"], goal: "Engagement" },
  { day: "Tuesday", templates: ["overvalued_player", "buy_before_obvious"], goal: "Authority" },
  { day: "Wednesday", templates: ["contender_rebuilder", "even_or_not"], goal: "Trade Education" },
  { day: "Thursday", templates: ["stop_doing_this", "dynasty_truth"], goal: "Snarky Strategy" },
  { day: "Friday", templates: ["trade_finder_showcase"], goal: "App Conversion" },
];

const statusStyles = {
  scheduled: { icon: Clock, color: "#fbbf24", label: "SCHEDULED" },
  published: { icon: CheckCircle, color: "#4ade80", label: "PUBLISHED" },
  failed: { icon: WarningCircle, color: "#ff3b30", label: "FAILED" },
  ready_no_token: { icon: WarningCircle, color: "#fbbf24", label: "READY" },
};

// Helper to get auth headers from localStorage
function getAuthHeaders() {
  try {
    const stored = localStorage.getItem("dynastydeal_user");
    if (stored) {
      // The session token is stored in cookies, but we also need to pass withCredentials
      return {};
    }
  } catch (e) {}
  return {};
}

// Create axios instance that always sends credentials
// Use the global axios instance (which has the Authorization interceptor)
const authAxios = axios;

export default function PostGenerator({ user }) {
  const [templates, setTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState("dynasty_truth");
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [generatedPost, setGeneratedPost] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [imageSuggestions, setImageSuggestions] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("12:00");
  const [useAutoData, setUseAutoData] = useState(true);
  const [dataUsed, setDataUsed] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageMode, setImageMode] = useState("template");

  useEffect(() => {
    authAxios.get(`${API}/admin/post-templates`).then(r => setTemplates(r.data.templates || {})).catch(e => {
      console.error("Failed to load templates:", e.response?.status, e.response?.data);
      toast.error("Failed to load templates — check admin access");
    });
    fetchScheduled();
  }, []);

  useEffect(() => {
    if (!selectedTemplate) return;
    authAxios.get(`${API}/admin/image-suggestions/${selectedTemplate}`).then(r => {
      setImageSuggestions(r.data.suggestions || []);
      setSelectedImage(null);
    }).catch(() => {});
  }, [selectedTemplate]);

  const fetchScheduled = () => {
    authAxios.get(`${API}/admin/scheduled-posts`).then(r => setScheduledPosts(r.data || [])).catch(() => {});
  };

  const handleGenerate = async () => {
    setLoading(true);
    setGeneratedPost("");
    setDataUsed(null);
    try {
      const endpoint = useAutoData ? `${API}/admin/generate-post-auto` : `${API}/admin/generate-post`;
      const res = await authAxios.post(endpoint, {
        template_id: selectedTemplate,
        topic,
        custom_notes: notes,
      });
      setGeneratedPost(res.data.post);
      if (res.data.data_used) setDataUsed(res.data.data_used);
      toast.success("Post generated with " + (useAutoData ? "live player data!" : "custom topic!"));
    } catch (e) {
      console.error("Generation failed:", e.response?.status, e.response?.data);
      toast.error(e.response?.data?.detail || "Generation failed — check admin access");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedPost);
    setCopied(true);
    toast.success("Copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGenerateImage = async (mode) => {
    if (!generatedPost) { toast.error("Generate a post first"); return; }
    setImageLoading(true);
    setGeneratedImage(null);
    try {
      const templateType = selectedImage?.type || "text_card";
      
      const res = await authAxios.post(`${API}/admin/generate-image`, {
        mode,
        post_content: generatedPost,
        template_id: selectedTemplate,
        image_style: selectedImage?.desc || "",
        template_type: templateType,
        players: [],  // Let backend extract players from post text
      });
      
      if (mode === "ai" && res.data.image_base64) {
        setGeneratedImage({ type: "ai", src: `data:image/png;base64,${res.data.image_base64}` });
        toast.success("AI image generated!");
      } else if (mode === "template" && res.data.image_base64) {
        setGeneratedImage({ type: "template_png", src: `data:image/png;base64,${res.data.image_base64}` });
        toast.success("Trade card generated!");
      } else if (mode === "template" && res.data.html) {
        setGeneratedImage({ type: "template_html", html: res.data.html });
        toast.info("Card generated (HTML preview — PNG rendering unavailable)");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image generation failed");
    } finally {
      setImageLoading(false);
    }
  };

  const downloadImage = () => {
    if (!generatedImage) return;
    if (generatedImage.src) {
      // PNG download (both AI and template_png)
      const link = document.createElement("a");
      link.href = generatedImage.src;
      link.download = `dynastydeal-${selectedTemplate}-${Date.now()}.png`;
      link.click();
      toast.success("Image downloaded!");
    } else if (generatedImage.html) {
      navigator.clipboard.writeText(generatedImage.html);
      toast.success("HTML copied to clipboard");
    }
  };

  const handleSchedule = async () => {
    if (!generatedPost) return;
    const scheduledFor = scheduleDate && scheduleTime ? `${scheduleDate}T${scheduleTime}:00` : "";
    try {
      await authAxios.post(`${API}/admin/schedule-post`, {
        content: generatedPost,
        template_id: selectedTemplate,
        template_name: templates[selectedTemplate]?.name || selectedTemplate,
        image_type: selectedImage?.type || "",
        image_note: selectedImage?.desc || "",
        scheduled_for: scheduledFor,
      });
      toast.success(scheduledFor ? "Scheduled!" : "Saved to queue!");
      fetchScheduled();
    } catch (e) {
      toast.error("Failed to schedule");
    }
  };

  const handlePublish = async (postId) => {
    try {
      const res = await authAxios.post(`${API}/admin/publish-post/${postId}`);
      toast[res.data.status === "published" ? "success" : "info"](res.data.message || res.data.status);
      fetchScheduled();
    } catch (e) { toast.error("Publish failed"); }
  };

  const handleDelete = async (postId) => {
    await authAxios.delete(`${API}/admin/scheduled-posts/${postId}`);
    fetchScheduled();
  };

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const todaySchedule = WEEKLY_SCHEDULE.find(d => d.day === today) || WEEKLY_SCHEDULE[0];
  const templateList = Object.entries(templates);

  if (!user?.is_admin) {
    const handleLogin = () => { const r = window.location.origin + '/'; window.location.href = '/api/auth/google/login'; };
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <ShieldCheck size={48} weight="thin" className="mx-auto text-[#333] mb-4" />
        <h2 className="font-heading text-2xl text-white uppercase mb-2">ADMIN ACCESS REQUIRED</h2>
        <p className="text-sm text-[#a3a3a3] mb-6">{user?.email ? 'Your account does not have admin privileges.' : 'Sign in with your Google account to access this page.'}</p>
        {!user?.email && (
          <button onClick={handleLogin} className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-bold text-sm rounded-sm hover:bg-gray-100" data-testid="admin-google-signin">
            <GoogleLogo size={16} weight="bold" /> SIGN IN WITH GOOGLE
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-6 sm:py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl tracking-tight text-white uppercase" data-testid="post-gen-title">
            POST <span className="text-[#ccff00]">GENERATOR</span>
          </h1>
          <p className="text-sm text-[#a3a3a3] mt-1">Create Facebook posts with live FantasyCalc data</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-[#666]">TODAY'S FOCUS</p>
          <p className="text-xs text-[#ccff00] font-bold">{todaySchedule.day}: {todaySchedule.goal}</p>
        </div>
      </div>

      <Tabs defaultValue="create">
        <TabsList className="bg-[#171717] border border-[#262626] rounded-sm mb-4">
          <TabsTrigger value="create" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-[#ccff00] data-[state=active]:text-black">CREATE</TabsTrigger>
          <TabsTrigger value="queue" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-[#ccff00] data-[state=active]:text-black">QUEUE ({scheduledPosts.length})</TabsTrigger>
          <TabsTrigger value="plan" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-[#ccff00] data-[state=active]:text-black">WEEKLY PLAN</TabsTrigger>
        </TabsList>

        <TabsContent value="create">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 space-y-4">
              {/* Data Mode Toggle */}
              <div className="flex border border-[#262626] rounded-sm overflow-hidden">
                <button onClick={() => setUseAutoData(true)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-xs font-bold uppercase ${useAutoData ? 'bg-[#ccff00] text-black' : 'bg-[#171717] text-[#a3a3a3] hover:text-white'}`} data-testid="mode-auto">
                  <Database size={14} weight="bold" /> LIVE DATA
                </button>
                <button onClick={() => setUseAutoData(false)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-xs font-bold uppercase ${!useAutoData ? 'bg-[#ccff00] text-black' : 'bg-[#171717] text-[#a3a3a3] hover:text-white'}`} data-testid="mode-manual">
                  <PencilSimple size={14} weight="bold" /> CUSTOM TOPIC
                </button>
              </div>

              {useAutoData && (
                <div className="border border-[#4ade80]/30 bg-[#4ade80]/5 p-3">
                  <p className="text-[10px] font-bold uppercase text-[#4ade80] mb-1">LIVE DATA MODE</p>
                  <p className="text-xs text-[#a3a3a3]">AI pulls real-time player values, trends, and rankings from FantasyCalc to create posts with actual dynasty data. Players and values will be accurate.</p>
                </div>
              )}

              {/* Template */}
              <div className="border border-[#262626] bg-[#171717] p-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-3">TEMPLATE ({templateList.length} available)</p>
                {templateList.length > 0 ? (
                  <>
                    <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                      <SelectTrigger className="w-full h-10 bg-[#0a0a0a] border-[#262626] text-sm text-white" data-testid="template-selector">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#171717] border-[#262626]">
                        {templateList.map(([id, t]) => (
                          <SelectItem key={id} value={id} className="text-white hover:bg-[#262626]">
                            {t.name} <span className="text-[10px] text-[#666] ml-1">({t.pillar})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-1.5 mt-3 flex-wrap">
                      <span className="text-[10px] text-[#666] mr-1">Today:</span>
                      {todaySchedule.templates.map(tid => (
                        <button key={tid} onClick={() => setSelectedTemplate(tid)} className={`text-[10px] px-2 py-1 border rounded-sm ${selectedTemplate === tid ? 'border-[#ccff00] text-[#ccff00] bg-[#ccff00]/10' : 'border-[#262626] text-[#666] hover:border-[#444]'}`}>
                          {templates[tid]?.name || tid}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-[#ff3b30]">Templates failed to load. Make sure you're logged in as admin via Google OAuth.</p>
                )}
              </div>

              {/* Optional Inputs */}
              <div className="border border-[#262626] bg-[#171717] p-5 space-y-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-2">{useAutoData ? 'FOCUS TOPIC (optional — AI picks best angle if blank)' : 'TOPIC'}</p>
                  <Input placeholder={useAutoData ? "e.g. rising players, overvalued RBs, draft picks..." : "e.g. CeeDee Lamb's value after injury..."} value={topic} onChange={(e) => setTopic(e.target.value)} className="h-10 bg-[#0a0a0a] border-[#262626] text-white text-sm placeholder:text-[#444]" data-testid="topic-input" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-2">NOTES (optional)</p>
                  <Input placeholder="Any specific angle, hot take, or context..." value={notes} onChange={(e) => setNotes(e.target.value)} className="h-10 bg-[#0a0a0a] border-[#262626] text-white text-sm placeholder:text-[#444]" />
                </div>
              </div>

              {/* Image Generation — Always visible after post is generated */}
              <div className="border border-[#262626] bg-[#171717] p-5" data-testid="image-generation">
                <div className="flex items-center gap-2 mb-3">
                  <Image size={14} weight="bold" className="text-[#ccff00]" />
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666]">CREATE GRAPHIC</p>
                </div>
                
                {/* Template Type Selection */}
                <p className="text-[10px] text-[#666] mb-2">Choose a graphic style:</p>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {[
                    { id: "trade_card", label: "TRADE CARD", desc: "Sleeper-style trade screen with player headshots" },
                    { id: "player_spotlight", label: "PLAYER SPOTLIGHT", desc: "Single player focus with position and stats" },
                    { id: "value_chart", label: "VALUE CHART", desc: "Bar chart comparing player values" },
                    { id: "text_card", label: "TEXT CARD", desc: "Bold text statement on dark background" },
                  ].map(t => (
                    <button key={t.id} onClick={() => setSelectedImage({ type: t.id, desc: t.desc })} className={`text-left p-3 border transition-colors ${selectedImage?.type === t.id ? 'border-[#ccff00] bg-[#ccff00]/5' : 'border-[#262626] hover:border-[#444]'}`}>
                      <span className="text-[10px] font-bold uppercase text-[#ccff00]">{t.label}</span>
                      <p className="text-[10px] text-[#a3a3a3] mt-0.5">{t.desc}</p>
                    </button>
                  ))}
                </div>
                
                {/* Generate Buttons */}
                <div className="flex gap-2">
                  <Button onClick={() => { setImageMode("template"); handleGenerateImage("template"); }} disabled={imageLoading || !generatedPost} className="flex-1 h-9 bg-transparent border border-[#ccff00] text-[#ccff00] hover:bg-[#ccff00] hover:text-black rounded-sm text-[10px] uppercase" data-testid="gen-template-img">
                    {imageLoading && imageMode === "template" ? <div className="w-3 h-3 border-2 border-[#ccff00] border-t-transparent rounded-full animate-spin" /> : "GENERATE TEMPLATE"}
                  </Button>
                  <Button onClick={() => { setImageMode("ai"); handleGenerateImage("ai"); }} disabled={imageLoading || !generatedPost} className="flex-1 h-9 btn-primary rounded-sm text-[10px] uppercase" data-testid="gen-ai-img">
                    {imageLoading && imageMode === "ai" ? <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <><Lightning size={12} weight="bold" className="mr-1" /> AI IMAGE</>}
                  </Button>
                </div>
                <p className="text-[9px] text-[#666] mt-2">Template = instant Sleeper-style graphic. AI = custom creative (~30sec, uses OpenAI).</p>
                {!generatedPost && <p className="text-[9px] text-[#fbbf24] mt-1">Generate a post first, then create graphics.</p>}
              </div>

              <Button onClick={handleGenerate} disabled={loading || templateList.length === 0} className="w-full h-11 btn-primary rounded-sm text-sm" data-testid="generate-post-btn">
                {loading ? <div className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />GENERATING...</div> : <><Lightning size={16} weight="bold" className="mr-2" /> GENERATE {useAutoData ? 'WITH LIVE DATA' : 'POST'}</>}
              </Button>
            </div>

            {/* Output */}
            <div className="lg:col-span-7 space-y-4">
              {generatedPost ? (
                <>
                  <div className="border border-[#ccff00]/30 bg-[#171717] p-5" data-testid="generated-post">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#ccff00]">GENERATED POST</p>
                        {dataUsed && <span className="text-[8px] bg-[#4ade80]/10 text-[#4ade80] px-1.5 py-0.5 border border-[#4ade80]/30">LIVE DATA</span>}
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleGenerate} disabled={loading} className="h-8 px-3 bg-transparent border border-[#262626] text-[#a3a3a3] hover:border-[#ccff00] hover:text-[#ccff00] rounded-sm text-[10px]">
                          <ArrowsClockwise size={12} weight="bold" className="mr-1" /> REDO
                        </Button>
                        <Button onClick={copyToClipboard} className="h-8 px-3 btn-primary rounded-sm text-[10px]" data-testid="copy-post-btn">
                          {copied ? <><Check size={12} weight="bold" className="mr-1" /> COPIED</> : <><Copy size={12} weight="bold" className="mr-1" /> COPY</>}
                        </Button>
                      </div>
                    </div>
                    {/* Phone Preview */}
                    <div className="max-w-sm mx-auto">
                      <div className="bg-white rounded-lg p-4 shadow-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-[#ccff00] rounded-full flex items-center justify-center"><span className="text-black font-bold text-xs">DD</span></div>
                          <div><p className="text-xs font-bold text-gray-900">Dynasty Trade Maker</p><p className="text-[10px] text-gray-500">Just now</p></div>
                        </div>
                        <p className="text-sm text-gray-900 whitespace-pre-line leading-relaxed">{generatedPost}</p>
                        {selectedImage && (
                          <div className="mt-3 bg-gray-100 rounded p-3 border border-gray-200">
                            <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">{selectedImage.type.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-gray-600">{selectedImage.desc}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-3 justify-center">
                      <span className="text-[10px] text-[#666]">{generatedPost.length} chars</span>
                      {dataUsed?.rising?.length > 0 && <span className="text-[10px] text-[#4ade80]">Rising: {dataUsed.rising.join(", ")}</span>}
                    </div>
                  </div>

                  {/* Generated Image Preview */}
                  {generatedImage && (
                    <div className="border border-[#ccff00]/30 bg-[#171717] p-5" data-testid="image-preview">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Image size={14} weight="bold" className="text-[#ccff00]" />
                          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#ccff00]">
                            {generatedImage.type === "ai" ? "AI GENERATED IMAGE" : "TEMPLATE GRAPHIC"}
                          </p>
                        </div>
                        <Button onClick={downloadImage} className="h-7 px-3 btn-primary rounded-sm text-[9px]" data-testid="download-img-btn">
                          DOWNLOAD
                        </Button>
                      </div>
                      {generatedImage.src && (
                        <img src={generatedImage.src} alt="Generated post graphic" className="w-full max-w-md mx-auto rounded-sm border border-[#262626]" />
                      )}
                      {!generatedImage.src && generatedImage.html && (
                        <div className="bg-[#0a0a0a] border border-[#262626] rounded-sm overflow-hidden">
                          <div className="transform scale-[0.4] origin-top-left" style={{width: '1080px', height: '432px'}}>
                            <div dangerouslySetInnerHTML={{__html: generatedImage.html}} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Schedule */}
                  <div className="border border-[#262626] bg-[#171717] p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <CalendarBlank size={14} weight="bold" className="text-[#ccff00]" />
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666]">SCHEDULE</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex gap-2 flex-1">
                        <Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} className="h-10 bg-[#0a0a0a] border-[#262626] text-white text-sm flex-1" />
                        <Input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="h-10 bg-[#0a0a0a] border-[#262626] text-white text-sm w-28" />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleSchedule} className="h-10 px-4 bg-transparent border border-[#ccff00] text-[#ccff00] hover:bg-[#ccff00] hover:text-black rounded-sm text-xs uppercase">
                          <CalendarBlank size={14} weight="bold" className="mr-1" /> {scheduleDate ? 'SCHEDULE' : 'SAVE'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="border border-dashed border-[#262626] p-12 text-center">
                  <Lightning size={36} weight="thin" className="mx-auto text-[#333] mb-3" />
                  <p className="text-sm text-[#a3a3a3]">{useAutoData ? 'Select a template and click Generate — AI will use live FantasyCalc data' : 'Select a template, enter a topic, and generate'}</p>
                  <p className="text-[10px] text-[#666] mt-1">"The Sharp League Mate" persona — confident, snarky, football-first</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* QUEUE */}
        <TabsContent value="queue">
          {scheduledPosts.length === 0 ? (
            <div className="border border-dashed border-[#262626] p-12 text-center"><p className="text-[#a3a3a3] text-sm">No posts in queue</p></div>
          ) : (
            <div className="space-y-3">
              {scheduledPosts.map((post) => {
                const st = statusStyles[post.status] || statusStyles.scheduled;
                const StIcon = st.icon;
                return (
                  <div key={post.id} className="border border-[#262626] bg-[#171717] p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1 text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm" style={{ color: st.color, backgroundColor: `${st.color}15`, border: `1px solid ${st.color}30` }}>
                          <StIcon size={10} weight="bold" /> {st.label}
                        </span>
                        <span className="text-[10px] text-[#666]">{post.template_name}</span>
                        {post.scheduled_for && <span className="text-[10px] font-mono text-[#a3a3a3]">{new Date(post.scheduled_for).toLocaleString()}</span>}
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        {(post.status === "scheduled" || post.status === "ready_no_token") && (
                          <Button onClick={() => handlePublish(post.id)} className="h-7 px-2 btn-primary rounded-sm text-[9px]"><PaperPlaneTilt size={10} weight="bold" /></Button>
                        )}
                        <Button onClick={() => { navigator.clipboard.writeText(post.content); toast.success("Copied!"); }} className="h-7 px-2 bg-transparent border border-[#262626] text-[#666] hover:text-white rounded-sm text-[9px]"><Copy size={10} /></Button>
                        <Button onClick={() => handleDelete(post.id)} className="h-7 px-2 bg-transparent border border-[#262626] text-[#666] hover:text-[#ff3b30] rounded-sm text-[9px]"><Trash size={10} /></Button>
                      </div>
                    </div>
                    <p className="text-sm text-[#a3a3a3] whitespace-pre-line leading-relaxed">{post.content.length > 300 ? post.content.slice(0, 300) + "..." : post.content}</p>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* WEEKLY PLAN */}
        <TabsContent value="plan">
          <div className="max-w-2xl mx-auto space-y-4">
            {WEEKLY_SCHEDULE.map(d => (
              <div key={d.day} className={`border bg-[#171717] p-5 ${d.day === today ? 'border-[#ccff00]/30' : 'border-[#262626]'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`font-heading text-lg uppercase ${d.day === today ? 'text-[#ccff00]' : 'text-white'}`}>{d.day} {d.day === today && '(TODAY)'}</h3>
                  <span className="text-xs text-[#a3a3a3]">{d.goal}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {d.templates.map(tid => <span key={tid} className="text-xs font-mono text-[#a3a3a3] bg-[#0a0a0a] px-3 py-1.5 border border-[#262626]">{templates[tid]?.name || tid}</span>)}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
